import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';

import { RunStatus } from '../../../../../../../../../../backend/src/types/types';
import { useReprocessRun } from '@/api/mutations/useReprocessRun';
import { useGetFailedDocs } from '@/api/queries/useGetFailedDocs';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { FailedDocument } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { isRunOngoing } from '@/helpers/runs';
import { AttemptedSummary } from '@/new/components/attempted-summary';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { formatFileSize } from '@/new/components/file-uploader/utils';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

const columnHelper = createColumnHelper<FailedDocument>();
const EMPTY_FAILED_DOCS: FailedDocument[] = [];

const columns = [
  columnHelper.accessor('name', {
    header: () => <span>Name</span>,
    cell: (info) => <span className="text-gray-900">{info.getValue()}</span>,
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) => <span className="text-gray-500">{info.getValue() || '—'}</span>,
  }),
  columnHelper.accessor('size', {
    header: () => <span>Size</span>,
    cell: (info) => <span className="text-gray-500">{formatFileSize(info.getValue() ?? 0)}</span>,
  }),
  columnHelper.accessor('uploadDate', {
    header: () => <span>Upload Date</span>,
    cell: (info) => <span className="text-gray-500">{formatDate(info.getValue(), true, true)}</span>,
  }),
  columnHelper.accessor('pipelineErrorType', {
    header: () => <span>Error Reason</span>,
    cell: (info) => {
      const value = info.getValue();
      return <span className="text-gray-500">{value ? value : 'Unknown'}</span>;
    },
  }),
  columnHelper.display({
    id: 'eligibleForRerun',
    header: () => <span>Eligible for Reprocessing</span>,
    cell: (info) =>
      info.row.original.pipelineErrorType === null ? (
        <Icon className="lucide--check text-green-600" />
      ) : (
        <span className="text-gray-500">—</span>
      ),
  }),
];

interface RunDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  runId: string;
  onNavigateToRun: (runId: string) => void;
}

export function RunDetailModal({ open, onOpenChange, matterId, runId, onNavigateToRun }: RunDetailModalProps) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { data: runs, isLoading: isLoadingRuns } = useGetMatterRuns(matterId);
  const { data: failedDocs, isLoading } = useGetFailedDocs(runId);
  const { user } = useUserContext();
  const { data: matter } = useGetMatterV2(matterId);
  const { mutate: reprocessRun, isLoading: isReprocessing } = useReprocessRun(matterId);

  const run = runs?.find((r) => r.id === runId);
  const successCount = run?.successfulPagesCount ?? 0;
  const failedCount = run?.failedPagesCount ?? 0;
  const totalCount = successCount + failedCount;
  const isComplete = run?.status === RunStatus.Complete;
  const isFailedRun = !!run && run.status === RunStatus.Failed;
  const isCancelledRun = run?.status === RunStatus.Cancelled;
  const isApprovalTimeoutRun = run?.status === RunStatus.ApprovalTimeout;
  const isChildRun = !!run?.parentRunId;
  const isParentWithChild = !!run?.childRunId;
  // Only surface docs in the table once the run is complete — in-flight docs
  // from a still-running reprocess shouldn't render as failures, and a hard
  // run-level failure keeps the existing "failed before processing" message.
  const docsForTable = failedDocs ?? EMPTY_FAILED_DOCS;
  const hasFailures = isComplete && docsForTable.length > 0;

  const canReprocess =
    !!run &&
    (user?.role?.name === 'Administrator' || matter?.createdById === user?.id || run.createdBy?.id === user?.id);
  const reprocessableDocs = (failedDocs ?? []).filter((doc) => doc.pipelineErrorType === null);
  const hasInFlightRunOnCase =
    !!run && (runs ?? []).some((r) => r.id !== run.id && r.caseId === run.caseId && isRunOngoing(r));

  const rerunBlockedReason: string | null = (() => {
    if (!run) return null;
    if (!canReprocess) {
      return 'Only an org admin, the matter creator; or the user who started this run can reprocess it.';
    }
    if (!run.isReprocessable) {
      if (run.childRunId) return 'This run has already been reprocessed.';
      if (run.status !== RunStatus.Complete) return 'Only completed runs can be reprocessed.';
      return 'This run has no eligible documents to reprocess.';
    }
    if (hasInFlightRunOnCase) {
      return 'Cannot reprocess while another run is in progress on this matter.';
    }
    return null;
  })();
  const isRerunBlocked = rerunBlockedReason !== null;

  const table = useReactTable({
    data: docsForTable,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: false,
    state: { globalFilter },
    initialState: { pagination: { pageSize: 25 } },
  });

  const handleConfirmRerun = () => {
    if (!run) return;
    reprocessRun(run.id, {
      onSuccess: () => {
        toast.success('Reprocessing run enqueued');
        setIsConfirmOpen(false);
        onOpenChange(false);
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : '';
        toast.error(message || 'Could not enqueue reprocessing run. Please try again.');
      },
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={`flex max-h-[80vh] w-full flex-col !transition-none !duration-0 data-[state=open]:!duration-0 ${
            hasFailures ? 'max-w-6xl' : 'max-w-2xl'
          }`}
        >
          <DialogHeader>
            <DialogTitle>Run Details</DialogTitle>
            <DialogDescription>
              {run && (
                <span className="flex items-center gap-2">
                  <span>{formatDate(run.createdDate, true, true)}</span>
                  {run.createdBy?.email && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span>{run.createdBy.email}</span>
                    </>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <AttemptedSummary
              attempted={totalCount}
              successful={successCount}
              failed={failedCount}
              attemptedLabel="pages attempted"
              successLabel="pages successful"
              failedLabel="pages failed"
            />
            <div className="flex items-center gap-2">
              {run?.parentRunId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => onNavigateToRun(run.parentRunId!)}
                >
                  <Icon className="lucide--arrow-up h-3 w-3" />
                  View Original Run
                </Button>
              )}
              {run?.childRunId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => onNavigateToRun(run.childRunId!)}
                >
                  <Icon className="lucide--arrow-down h-3 w-3" />
                  View Reprocessing Run
                </Button>
              )}
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            {hasFailures ? (
              <>
                {isParentWithChild && (
                  <div className="mb-2 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5">
                    <Icon className="lucide--info shrink-0 text-blue-500" />
                    <span className="text-sm text-gray-800">
                      A reprocessing run has been triggered for this run. Remaining failed documents could not be
                      reprocessed.
                    </span>
                  </div>
                )}
                {isChildRun && (
                  <div className="mb-2 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5">
                    <Icon className="lucide--alert-triangle shrink-0 text-amber-500" />
                    <span className="text-sm text-gray-800">These documents failed again during reprocessing.</span>
                  </div>
                )}
                <ScrollableTable
                  table={table}
                  globalFilter={globalFilter}
                  setGlobalFilter={setGlobalFilter}
                  plural="failed documents"
                  loading={isLoading}
                  className="h-full max-h-[50vh] px-1 pt-1"
                  additionalButtons={
                    <div className="ml-auto flex items-center gap-2">
                      {isRerunBlocked ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0}>
                              <Button variant="primary" disabled>
                                <Icon className="lucide--rotate-ccw" />
                                Reprocess failed documents
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-m text-xs">{rerunBlockedReason}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button variant="primary" onClick={() => setIsConfirmOpen(true)}>
                          <Icon className="lucide--rotate-ccw" />
                          Reprocess failed documents
                        </Button>
                      )}
                    </div>
                  }
                />
              </>
            ) : isLoading || isLoadingRuns || !run ? (
              <div className="my-2 flex items-center rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5">
                <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div
                className={`my-2 flex items-center rounded-lg border px-3.5 py-2.5 ${
                  isFailedRun
                    ? 'border-red-200 bg-red-50'
                    : isCancelledRun || isApprovalTimeoutRun
                      ? 'border-amber-200 bg-amber-50'
                      : !isComplete
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-green-200 bg-green-50'
                }`}
              >
                {isFailedRun ? (
                  <div className="flex items-center gap-2">
                    <Icon className="lucide--x-circle size-3.5 text-red-500" />
                    <span className="text-sm text-gray-800">This run failed before any documents were processed.</span>
                  </div>
                ) : isCancelledRun ? (
                  <div className="flex items-center gap-2">
                    <Icon className="lucide--ban size-3.5 text-amber-500" />
                    <span className="text-sm text-gray-800">This run was cancelled.</span>
                  </div>
                ) : isApprovalTimeoutRun ? (
                  <div className="flex items-center gap-2">
                    <Icon className="lucide--clock-alert size-3.5 text-amber-500" />
                    <span className="text-sm text-gray-800">This run timed out awaiting approval.</span>
                  </div>
                ) : !isComplete ? (
                  <div className="flex items-center gap-2">
                    <Icon className="lucide--loader-circle size-3.5 animate-spin text-amber-500 duration-700" />
                    <span className="text-sm text-gray-800">This run is still in progress.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Icon className="lucide--check-circle size-3.5 text-green-500" />
                    <span className="text-sm text-gray-800">
                      {isChildRun
                        ? 'All reprocessed documents finished successfully.'
                        : isParentWithChild
                          ? 'All failed documents from this run have been moved to the reprocessing run.'
                          : 'All documents in this run were processed successfully.'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          <span className="flex items-center gap-2">
            <Icon className="lucide--alert-triangle size-4 !text-gray-500" />
            Reprocess failed documents?
          </span>
        }
        isLoading={isReprocessing}
        onConfirm={handleConfirmRerun}
      >
        <p className="text-sm leading-relaxed text-gray-600">
          This will reprocess{' '}
          <span className="font-medium text-gray-900">
            {reprocessableDocs.length.toLocaleString()} {reprocessableDocs.length === 1 ? 'document' : 'documents'}
          </span>{' '}
          from this run. You're only billed for pages that are successfully processed, and you{' '}
          <span className="font-medium text-gray-900">won't be double-charged</span> across runs. Documents that
          previously failed with a known error reason are skipped, and other uploads on this matter will be paused until
          reprocessing finishes.
        </p>
      </ConfirmationModal>
    </>
  );
}
